import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { useStore } from '../context/StoreContext';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { 
  AlertTriangle, Package, TrendingUp, Calendar, Store, 
  Loader2, RefreshCw, BarChart3, ShoppingCart, Mail, Bell
} from 'lucide-react';
import { toast } from 'sonner';
import { formatDate, formatDateTime } from '../utils/dateFormat';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

const OOSReport = () => {
  const { getAuthHeader } = useAuth();
  const { selectedStore, storeName } = useStore();
  const [loading, setLoading] = useState(true);
  const [report, setReport] = useState(null);
  const [days, setDays] = useState('30');
  const [storeFilter, setStoreFilter] = useState('all');
  const [alertSettings, setAlertSettings] = useState(null);
  const [sendingTest, setSendingTest] = useState(false);

  const fetchReport = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ days });
      if (storeFilter !== 'all') {
        params.append('store', storeFilter);
      }
      const [reportRes, alertRes] = await Promise.all([
        axios.get(`${API_URL}/sales/oos-report?${params}`, { headers: getAuthHeader() }),
        axios.get(`${API_URL}/sales/oos-alert-settings`, { headers: getAuthHeader() })
      ]);
      setReport(reportRes.data);
      setAlertSettings(alertRes.data);
    } catch (error) {
      toast.error('Failed to load OOS report');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const sendTestEmail = async () => {
    setSendingTest(true);
    try {
      await axios.post(`${API_URL}/sales/test-oos-alert`, {}, { headers: getAuthHeader() });
      toast.success('Test email sent successfully!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send test email');
    } finally {
      setSendingTest(false);
    }
  };

  useEffect(() => {
    fetchReport();
  }, [days, storeFilter]);

  if (loading && !report) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Loader2 className="w-8 h-8 animate-spin text-slate-400" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="oos-report-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-heading text-3xl font-bold uppercase tracking-tight text-slate-900 flex items-center gap-3">
            <AlertTriangle className="w-8 h-8 text-red-500" />
            Negative Stock Report
          </h1>
          <p className="text-slate-500 mt-1">Track products sold when stock was zero or negative</p>
        </div>
        <Button 
          onClick={fetchReport} 
          variant="outline"
          disabled={loading}
          data-testid="refresh-report"
        >
          <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
          Refresh
        </Button>
      </div>

      {/* Filters */}
      <div className="flex gap-4">
        <Select value={days} onValueChange={setDays}>
          <SelectTrigger className="w-[160px]" data-testid="days-filter">
            <Calendar className="w-4 h-4 mr-2" />
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="7">Last 7 Days</SelectItem>
            <SelectItem value="30">Last 30 Days</SelectItem>
            <SelectItem value="90">Last 90 Days</SelectItem>
            <SelectItem value="365">Last Year</SelectItem>
          </SelectContent>
        </Select>
        
        <Select value={storeFilter} onValueChange={setStoreFilter}>
          <SelectTrigger className="w-[160px]" data-testid="store-filter">
            <Store className="w-4 h-4 mr-2" />
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Stores</SelectItem>
            <SelectItem value="orewa">{storeName('orewa')}</SelectItem>
            <SelectItem value="eastridge">{storeName('eastridge')}</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card className="border-red-200 bg-red-50">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-red-600">OOS Sales</p>
                <p className="text-3xl font-bold text-red-700">
                  {report?.summary?.total_oos_sales || 0}
                </p>
                <p className="text-xs text-red-500 mt-1">transactions with OOS items</p>
              </div>
              <ShoppingCart className="w-10 h-10 text-red-300" />
            </div>
          </CardContent>
        </Card>
        
        <Card className="border-amber-200 bg-amber-50">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-amber-600">Items Sold OOS</p>
                <p className="text-3xl font-bold text-amber-700">
                  {report?.summary?.total_oos_items_sold || 0}
                </p>
                <p className="text-xs text-amber-500 mt-1">total units sold when out of stock</p>
              </div>
              <Package className="w-10 h-10 text-amber-300" />
            </div>
          </CardContent>
        </Card>
        
        <Card className="border-slate-200 bg-slate-50">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-slate-600">Unique Products</p>
                <p className="text-3xl font-bold text-slate-700">
                  {report?.summary?.unique_products_sold_oos || 0}
                </p>
                <p className="text-xs text-slate-500 mt-1">different products sold OOS</p>
              </div>
              <BarChart3 className="w-10 h-10 text-slate-300" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Tabs for different views */}
      <Tabs defaultValue="frequent" className="space-y-4">
        <TabsList>
          <TabsTrigger value="frequent" className="flex items-center gap-2">
            <TrendingUp className="w-4 h-4" />
            Frequent OOS Products
          </TabsTrigger>
          <TabsTrigger value="recent" className="flex items-center gap-2">
            <Calendar className="w-4 h-4" />
            Recent OOS Sales
          </TabsTrigger>
        </TabsList>

        {/* Frequent OOS Products Tab */}
        <TabsContent value="frequent">
          <Card className="border rounded-sm">
            <CardHeader className="border-b bg-slate-50">
              <CardTitle className="font-heading text-lg font-bold uppercase flex items-center gap-2">
                <TrendingUp className="w-5 h-5 text-red-500" />
                Products Frequently Sold Out of Stock
              </CardTitle>
              <p className="text-sm text-slate-500">
                These products are often sold when stock shows 0 - may indicate stock count issues
              </p>
            </CardHeader>
            <CardContent className="p-0">
              <Table>
                <TableHeader>
                  <TableRow className="bg-slate-50">
                    <TableHead className="font-heading font-bold uppercase text-xs">Product</TableHead>
                    <TableHead className="font-heading font-bold uppercase text-xs text-center">Times Sold OOS</TableHead>
                    <TableHead className="font-heading font-bold uppercase text-xs text-center">Total Units</TableHead>
                    <TableHead className="font-heading font-bold uppercase text-xs">Action</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {(!report?.frequent_oos_products || report.frequent_oos_products.length === 0) ? (
                    <TableRow>
                      <TableCell colSpan={4} className="text-center py-12 text-slate-500">
                        <AlertTriangle className="w-12 h-12 mx-auto mb-3 text-slate-300" />
                        No OOS sales recorded in this period
                      </TableCell>
                    </TableRow>
                  ) : (
                    report.frequent_oos_products.map((product, index) => (
                      <TableRow key={product.product_id} className={index < 3 ? 'bg-red-50' : ''}>
                        <TableCell className="font-medium">
                          {product.product_name}
                          {index < 3 && (
                            <Badge className="ml-2 bg-red-500 text-xs">Top {index + 1}</Badge>
                          )}
                        </TableCell>
                        <TableCell className="text-center">
                          <Badge variant="outline" className="bg-red-100 text-red-700 border-red-200">
                            {product.times_sold_oos}x
                          </Badge>
                        </TableCell>
                        <TableCell className="text-center font-bold">
                          {product.total_units}
                        </TableCell>
                        <TableCell>
                          <Button 
                            size="sm" 
                            variant="outline"
                            className="text-blue-600 border-blue-200 hover:bg-blue-50"
                            onClick={() => window.location.href = '/stock-count'}
                          >
                            Do Stock Count
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Recent OOS Sales Tab */}
        <TabsContent value="recent">
          <Card className="border rounded-sm">
            <CardHeader className="border-b bg-slate-50">
              <CardTitle className="font-heading text-lg font-bold uppercase flex items-center gap-2">
                <Calendar className="w-5 h-5 text-amber-500" />
                Recent Sales with OOS Items
              </CardTitle>
            </CardHeader>
            <CardContent className="p-0">
              <Table>
                <TableHeader>
                  <TableRow className="bg-slate-50">
                    <TableHead className="font-heading font-bold uppercase text-xs">Date</TableHead>
                    <TableHead className="font-heading font-bold uppercase text-xs">Store</TableHead>
                    <TableHead className="font-heading font-bold uppercase text-xs">OOS Items</TableHead>
                    <TableHead className="font-heading font-bold uppercase text-xs text-right">Total</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {(!report?.recent_oos_sales || report.recent_oos_sales.length === 0) ? (
                    <TableRow>
                      <TableCell colSpan={4} className="text-center py-12 text-slate-500">
                        <ShoppingCart className="w-12 h-12 mx-auto mb-3 text-slate-300" />
                        No OOS sales in this period
                      </TableCell>
                    </TableRow>
                  ) : (
                    report.recent_oos_sales.map((sale) => (
                      <TableRow key={sale.id}>
                        <TableCell className="text-sm">
                          {formatDateTime(sale.created_at)}
                        </TableCell>
                        <TableCell>
                          <Badge 
                            variant="outline" 
                            className={sale.store === 'eastridge' 
                              ? 'bg-blue-50 text-blue-700 border-blue-200' 
                              : 'bg-emerald-50 text-emerald-700 border-emerald-200'
                            }
                          >
                            {storeName(sale.store)}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          <div className="space-y-1">
                            {(sale.oos_items || []).map((item, idx) => (
                              <div key={idx} className="flex items-center gap-2">
                                <Badge className="bg-red-100 text-red-700 text-xs">OOS</Badge>
                                <span className="text-sm">{item.product_name}</span>
                                <span className="text-xs text-slate-500">x{item.quantity}</span>
                              </div>
                            ))}
                          </div>
                        </TableCell>
                        <TableCell className="text-right font-bold">
                          ${sale.total_amount?.toFixed(2)}
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Insight Card */}
      {report?.summary?.total_oos_sales > 0 && (
        <Card className="border-amber-300 bg-amber-50">
          <CardContent className="p-4">
            <div className="flex items-start gap-3">
              <AlertTriangle className="w-6 h-6 text-amber-600 flex-shrink-0 mt-0.5" />
              <div>
                <p className="font-bold text-amber-800">Stock Count Recommendation</p>
                <p className="text-sm text-amber-700 mt-1">
                  You have {report.summary.unique_products_sold_oos} product(s) being sold as out-of-stock. 
                  This often indicates the physical inventory doesn't match the system. 
                  Consider doing a stock count for these items to ensure accurate inventory levels.
                </p>
                <Button 
                  className="mt-3 bg-amber-600 hover:bg-amber-700"
                  onClick={() => window.location.href = '/stock-count'}
                >
                  Go to Stock Count
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Email Alert Settings Card */}
      <Card className="border-blue-200 bg-blue-50">
        <CardContent className="p-4">
          <div className="flex items-start justify-between">
            <div className="flex items-start gap-3">
              <Bell className="w-6 h-6 text-blue-600 flex-shrink-0 mt-0.5" />
              <div>
                <p className="font-bold text-blue-800">Email Alerts</p>
                <p className="text-sm text-blue-700 mt-1">
                  {alertSettings?.email_configured ? (
                    <>
                      Alerts will be sent to <strong>{alertSettings.alert_email}</strong> when any product 
                      is sold OOS <strong>{alertSettings.threshold}+ times</strong>.
                    </>
                  ) : (
                    <>
                      Email alerts are not configured. Add your <strong>RESEND_API_KEY</strong> to the backend .env file to enable alerts.
                    </>
                  )}
                </p>
                {alertSettings?.email_configured && (
                  <Button 
                    variant="outline"
                    size="sm"
                    className="mt-3 border-blue-300 text-blue-700 hover:bg-blue-100"
                    onClick={sendTestEmail}
                    disabled={sendingTest}
                    data-testid="test-email-btn"
                  >
                    {sendingTest ? (
                      <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Sending...</>
                    ) : (
                      <><Mail className="w-4 h-4 mr-2" /> Send Test Email</>
                    )}
                  </Button>
                )}
              </div>
            </div>
            <Badge className={alertSettings?.email_configured ? 'bg-green-500' : 'bg-slate-400'}>
              {alertSettings?.email_configured ? 'Active' : 'Not Configured'}
            </Badge>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default OOSReport;
